import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { logout } from '../utils/auth';

export interface GroupClassParticipant {
  id: number;
  name: string;
  surnameInitial: string;
}

export interface GroupClassEntry {
  classId: number;
  instanceId: number;
  title: string;
  description: string;
  host: string;
  color: string;
  isCyclical: boolean;
  durationMinutes: number;
  maxMembers: number;
  minMembers: number;
  resignWindowHours: number;
  date: string;
  signupCount: number;
  isSignedUp: boolean;
  participants: GroupClassParticipant[];
}

export interface GroupClassesResponse {
  classes: GroupClassEntry[];
  enabled: boolean;
}

const fetchGroupClasses = async (): Promise<GroupClassesResponse> => {
  const apiKey = localStorage.getItem('apiKey');
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_STUDIO_API_URL}/pwa/group-classes`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );
    return data;
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.data?.error === 'Invalid or missing API key') {
      logout();
    }
    throw error;
  }
};

export const useGroupClasses = () => {
  return useQuery<GroupClassesResponse, Error>({
    queryKey: ['groupClasses'],
    queryFn: fetchGroupClasses,
  });
};

const fetchGroupClassDetail = async (instanceId: number): Promise<GroupClassEntry> => {
  const apiKey = localStorage.getItem('apiKey');
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_STUDIO_API_URL}/pwa/group-classes/detail?instanceId=${instanceId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );
    return data;
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.data?.error === 'Invalid or missing API key') {
      logout();
    }
    throw error;
  }
};

export const useGroupClassDetail = (instanceId: number) => {
  return useQuery<GroupClassEntry, Error>({
    queryKey: ['groupClassDetail', instanceId],
    queryFn: () => fetchGroupClassDetail(instanceId),
    enabled: !!instanceId,
  });
};

export interface SignupResponse {
  success: boolean;
  warning?: boolean;
  restricted?: boolean;
  absenceCount?: number;
  onePerDayRestricted?: boolean;
}

export const useGroupClassSignup = () => {
  const queryClient = useQueryClient();

  const signup = useMutation<SignupResponse, Error, number>({
    mutationFn: async (instanceId: number) => {
      const apiKey = localStorage.getItem('apiKey');
      const { data } = await axios.post(
        `${process.env.REACT_APP_STUDIO_API_URL}/pwa/group-classes/signup`,
        { instanceId },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      return data as SignupResponse;
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['groupClasses'] });
        queryClient.invalidateQueries({ queryKey: ['groupClassDetail'] });
      }
    },
  });

  const signout = useMutation({
    mutationFn: async (instanceId: number) => {
      const apiKey = localStorage.getItem('apiKey');
      const { data } = await axios.delete(
        `${process.env.REACT_APP_STUDIO_API_URL}/pwa/group-classes/signup`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
          data: { instanceId },
        }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupClasses'] });
      queryClient.invalidateQueries({ queryKey: ['groupClassDetail'] });
    },
  });

  return { signup, signout };
};
